import LibraryIndexChunk from "../../../classes/LibraryIndexChunk";

// The Document Library no longer listens to every folder and file a user can
// open (25,000 documents on production, more than the emulator's browser
// channel will deliver at all). It listens to:
//  - the name index, for the tree, search and paths (LISTEN_LIBRARY_INDEX);
//  - the documents at the location on screen (LISTEN_LIBRARY_LOCATION);
//  - the chain of folders above it (ENSURE_LIBRARY_FOLDER_PATH);
//  - and, as before, the user's whole personal tier (Library.vue).
// Writes that must see more than that fetch it first
// (GET_LIBRARY_FOLDER_CONTENTS, GET_LIBRARY_SUBTREE).

// Folders nest ten deep on production; this only bounds a damaged chain.
const MAX_FOLDER_DEPTH = 100;
// Firestore's cap on the values of one `in` filter.
const IN_LIMIT = 30;

// The where clauses every Library query needs before the rules will run it:
// the tier, and for the personal tier the owner too.
export function libraryTierScope(tier, userID) {
  const scope = [["tier", "==", tier]];
  if (tier === "personal") scope.push(["createdBy", "==", userID]);
  return scope;
}

// A folder's direct subfolders and files, fetched into the store: for writes
// that must see all of them (reusing a subfolder by name, spotting a file
// that is about to be uploaded twice) when the folder is not the one on
// screen.
export const GET_LIBRARY_FOLDER_CONTENTS = async (
  { dispatch, state },
  { folder, folders = true, files = true }
) => {
  const scope = libraryTierScope(folder.data.tier, state.user.id);
  const [subfolders, contents] = await Promise.all([
    folders
      ? dispatch("GET_LIBRARY_FOLDERS", {
          where: [...scope, ["parentID", "==", folder.id]],
        })
      : [],
    files
      ? dispatch("GET_LIBRARY_FILES", {
          where: [...scope, ["folderID", "==", folder.id]],
        })
      : [],
  ]);
  return { folders: subfolders || [], files: contents || [] };
};

// Every folder below `folder` (breadth-first, not including it) and, unless
// `withFiles` is false, every file in any of them, Trash included -- fetched
// into the store level by level. Deleting a folder or changing its
// read-only flag must reach the whole subtree, not just what happens to be
// loaded.
export const GET_LIBRARY_SUBTREE = async (
  { dispatch, state },
  { folder, withFiles = true }
) => {
  const scope = libraryTierScope(folder.data.tier, state.user.id);
  const folders = [];
  const files = [];
  const seen = new Set([folder.id]);
  let level = [folder.id];
  while (level.length) {
    const nextLevel = [];
    for (let i = 0; i < level.length; i += IN_LIMIT) {
      const ids = level.slice(i, i + IN_LIMIT);
      const [subfolders, contents] = await Promise.all([
        dispatch("GET_LIBRARY_FOLDERS", {
          where: [...scope, ["parentID", "in", ids]],
        }),
        withFiles
          ? dispatch("GET_LIBRARY_FILES", {
              where: [...scope, ["folderID", "in", ids]],
            })
          : [],
      ]);
      (subfolders || []).forEach((subfolder) => {
        if (seen.has(subfolder.id)) return;
        seen.add(subfolder.id);
        folders.push(subfolder);
        nextLevel.push(subfolder.id);
      });
      files.push(...(contents || []));
    }
    level = nextLevel;
  }
  return { folders, files };
};

export const LISTEN_LIBRARY_INDEX = ({ dispatch }, { tiers, next, error }) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: LibraryIndexChunk,
    path: LibraryIndexChunk.getPath(),
    stateKey: "libraryIndex",
    listenerPath: "library-index-" + tiers.join("-"),
    // The rules read a chunk's tier, so the query must name the tiers.
    where: [["tier", "in", tiers]],
    next,
    error,
  });
};

// The folder and every folder above it, fetched where not already loaded.
// The breadcrumb, path labels and read-only inheritance all walk this chain.
export const ENSURE_LIBRARY_FOLDER_PATH = async ({ dispatch }, id) => {
  const seen = new Set();
  let cursor = id;
  while (cursor && !seen.has(cursor) && seen.size < MAX_FOLDER_DEPTH) {
    seen.add(cursor);
    const folder = await dispatch("GET_LIBRARY_FOLDER", cursor);
    cursor = (folder && folder.data.parentID) || null;
  }
};

// The documents one view of the Library shows: a shared tier's root or one
// of its folders (the subfolders and files directly inside), or Trash
// (trashed files in the shared tiers -- only the user's own unless they are
// an admin). The personal tier needs nothing here; Library.vue loads all of
// it.
//
// Listeners leave their documents in the store when they stop, so a folder
// deleted while nobody was looking at its parent would linger. The
// location's documents are dropped before listening again; `next` fires
// once every listener has delivered its first snapshot.
//
// Returns the listener handles, for UNLISTEN.
export const LISTEN_LIBRARY_LOCATION = async (
  { commit, dispatch, state },
  { tier, folderID = null, trash = false, tiers, next, error }
) => {
  const user = state.user;
  const specs = [];
  if (trash) {
    const where = [
      ["tier", "in", tiers],
      ["deletedAt", "!=", null],
    ];
    if (!user.isAdmin) where.push(["deletedBy", "==", user.id]);
    specs.push({
      action: "LISTEN_LIBRARY_FILES",
      listenerPath: `library-trash-${tiers.join("-")}${
        user.isAdmin ? "" : `-${user.id}`
      }`,
      where,
      stale: (file) =>
        tiers.includes(file.data.tier) && Boolean(file.data.deletedAt),
      stateKey: "libraryFiles",
    });
  } else if (tier && tier !== "personal") {
    const at = folderID || "root";
    specs.push(
      {
        action: "LISTEN_LIBRARY_FOLDERS",
        listenerPath: `library-folders-${tier}-${at}`,
        where: [
          ["tier", "==", tier],
          ["parentID", "==", folderID],
        ],
        stale: (folder) =>
          folder.data.tier === tier &&
          (folder.data.parentID || null) === folderID,
        stateKey: "libraryFolders",
      },
      {
        action: "LISTEN_LIBRARY_FILES",
        listenerPath: `library-files-${tier}-${at}`,
        where: [
          ["tier", "==", tier],
          ["folderID", "==", folderID],
        ],
        stale: (file) =>
          file.data.tier === tier && (file.data.folderID || null) === folderID,
        stateKey: "libraryFiles",
      }
    );
  }

  let waiting = specs.length;
  const settle = () => {
    waiting -= 1;
    if (waiting === 0 && next) next();
  };
  if (!waiting && next) next();

  const handles = [];
  for (const spec of specs) {
    // Another view that is still open (a second tab's router-view) may own
    // this listener; its documents are current, so leave them be.
    if (!state.listeners[spec.listenerPath]) {
      const stateIDs = Object.values(state[spec.stateKey] || {})
        .filter(spec.stale)
        .map((doc) => doc.id);
      commit("REMOVE_MANY", { stateKey: spec.stateKey, stateIDs });
    }
    let settled = false;
    const once = () => {
      if (settled) return;
      settled = true;
      settle();
    };
    handles.push(
      await dispatch(spec.action, {
        listenerPath: spec.listenerPath,
        where: spec.where,
        next: once,
        error: (err) => {
          once();
          if (error) error(err);
        },
      })
    );
  }
  return handles;
};
